import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Users, Clock, Shield, CheckCircle2, Target, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest standards in every project, from a single room to hundreds of hotel rooms.'
    },
    {
      icon: Shield,
      title: 'Reliability',
      description: 'Count on us to deliver consistent quality, on time, every time. Our reputation is built on trust.'
    },
    {
      icon: Heart,
      title: 'Care',
      description: 'We treat every property as if it were our own, with attention to detail and genuine care.'
    },
    {
      icon: Target,
      title: 'Precision',
      description: 'Our systematic approach ensures nothing is overlooked. Every corner, every surface, every time.'
    }
  ];

  const milestones = [
    { year: '2000', event: 'ACH Cleaning Company founded by Maria Chiman' },
    { year: '2005', event: 'Expanded into commercial and construction cleaning' },
    { year: '2010', event: 'Completed first 100+ room hotel project' },
    { year: '2015', event: 'Reached 500+ satisfied residential clients' },
    { year: '2020', event: 'Became preferred vendor for major Austin developers' },
    { year: '2024', event: 'Celebrating 25+ years of excellence' }
  ];

  const capabilities = [
    'Single-family homes',
    'Luxury estates',
    'Apartments & condos',
    'Hotel rooms (200+)',
    'Apartment complexes',
    'Office buildings',
    'Construction sites',
    'Commercial spaces'
  ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-brand-surface" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="stagger-children">
              <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-4">
                About ACH Cleaning
              </p>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-brand-navy tracking-tight leading-tight mb-6">
                Cleaning Excellence<br />
                <span className="text-brand-slate">Since 2000</span>
              </h1>
              <p className="font-body text-lg text-brand-charcoal leading-relaxed mb-8">
                Founded by Maria Chiman, ACH Cleaning Company has grown from a residential cleaning service to a comprehensive cleaning partner for homes, businesses, and large-scale construction projects across the Greater Austin Area.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/services">
                  <Button 
                    className="bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full px-6 btn-shine"
                    data-testid="about-services-btn"
                  >
                    Our Services
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/projects">
                  <Button 
                    variant="outline"
                    className="border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-body font-medium rounded-full px-6"
                    data-testid="about-projects-btn"
                  >
                    View Projects
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1756199638047-6d6930280e37?w=800&q=80" 
                  alt="Professional cleaning team"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating stat card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-navy rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-2xl text-brand-navy">1000+</p>
                    <p className="font-body text-sm text-brand-charcoal">Happy Clients</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maria's Story */}
      <section className="section-padding bg-white" data-testid="owner-story">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="aspect-square rounded-2xl overflow-hidden bg-brand-surface">
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center p-8">
                    <div className="w-32 h-32 bg-brand-navy rounded-full flex items-center justify-center mx-auto mb-6">
                      <span className="font-heading font-bold text-5xl text-white">MC</span>
                    </div>
                    <h3 className="font-heading font-bold text-xl text-brand-navy">Maria Chiman</h3>
                    <p className="font-body text-brand-charcoal">Founder & Owner</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-4">
                Our Story
              </p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight mb-6">
                Built on Hard Work & Dedication
              </h2>
              <div className="space-y-4 font-body text-brand-charcoal leading-relaxed">
                <p>
                  Maria Chiman founded ACH Cleaning Company with a simple vision: to provide cleaning services that she would be proud to receive in her own home. Starting with just residential clients, Maria's attention to detail and commitment to excellence quickly built a loyal customer base.
                </p>
                <p>
                  As word spread about ACH's exceptional quality, the company expanded into commercial cleaning and, eventually, large-scale post-construction projects. Today, ACH handles everything from weekly home cleaning to preparing hundreds of hotel rooms for grand openings.
                </p>
                <p>
                  What sets ACH apart is our ability to scale without compromising quality. Whether we're cleaning a single residence or a 200-room hotel, every space receives the same meticulous attention that built our reputation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding bg-brand-surface" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-16">
            <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-3">
              What Drives Us
            </p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 card-hover"
                data-testid={`value-card-${index}`}
              >
                <div className="w-12 h-12 bg-brand-navy/10 rounded-xl flex items-center justify-center mb-4">
                  <value.icon className="w-6 h-6 text-brand-navy" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-brand-navy mb-2">
                  {value.title}
                </h3>
                <p className="font-body text-sm text-brand-charcoal leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section-padding bg-white" data-testid="capabilities-section">
        <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-brand-accent font-body text-sm font-semibold uppercase tracking-widest mb-4">
                Our Capabilities
              </p>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight mb-6">
                No Project Too Big or Small
              </h2>
              <p className="font-body text-brand-charcoal leading-relaxed mb-8">
                From intimate residential spaces to massive commercial developments, ACH has the expertise, equipment, and team to handle any cleaning challenge. Our scalable approach means you get the same quality whether we're cleaning your home or your entire apartment complex.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                {capabilities.map((capability, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-brand-navy flex-shrink-0" />
                    <span className="font-body text-sm text-brand-charcoal">{capability}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1760072513442-9872656c1b07?w=600&q=80" 
                    alt="Residential cleaning"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1767793357572-7f282da502d3?w=600&q=80" 
                    alt="Construction cleaning"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-padding bg-brand-navy" data-testid="timeline-section">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-slate-400 font-body text-sm font-semibold uppercase tracking-widest mb-3">
              Our Journey
            </p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white tracking-tight">
              Key Milestones
            </h2>
          </div>

          <div className="space-y-6">
            {milestones.map((milestone, index) => (
              <div 
                key={index}
                className="flex items-center gap-6 group"
                data-testid={`milestone-${index}`}
              >
                <div className="w-20 flex-shrink-0">
                  <span className="font-heading font-bold text-xl text-white">{milestone.year}</span>
                </div>
                <div className="w-3 h-3 rounded-full bg-slate-500 group-hover:bg-white transition-colors flex-shrink-0"></div>
                <div className="flex-grow">
                  <p className="font-body text-slate-300 group-hover:text-white transition-colors">
                    {milestone.event}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-brand-surface" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-5 md:px-8 lg:px-12 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-navy tracking-tight mb-4">
            Ready to Work with Us?
          </h2>
          <p className="font-body text-brand-charcoal text-lg mb-8">
            Join over 1,000 satisfied clients who trust ACH for their cleaning needs.
          </p>
          <Link to="/#quote-form">
            <Button 
              className="bg-brand-navy hover:bg-brand-action text-white font-body font-medium rounded-full px-8 py-6 btn-shine"
              data-testid="about-cta-btn"
            >
              Get Your Free Quote
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
